<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';

export default {
  name: 'MessagePreview',
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
    showAttachmentPreview: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  computed: {
    messageType() {
      return this.message.message_type ?? this.message.messageType;
    },
    contentAttributes() {
      return this.message.content_attributes ?? this.message.contentAttributes;
    },
    messageByAgent() {
      return this.messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      return this.messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      return this.message.private ?? this.message.isPrivate;
    },
    parsedLastMessage() {
      const { email: { subject } = {} } = this.contentAttributes || {};
      return this.getPlainText(subject || this.message.content);
    },
    hasAttachments() {
      return Boolean(this.message.attachments?.length);
    },
    firstAttachment() {
      return this.message.attachments?.[0] || null;
    },
    lastMessageFileType() {
      return this.firstAttachment?.file_type ?? this.firstAttachment?.fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    attachmentPreviewUrl() {
      if (!this.showAttachmentPreview) {
        return '';
      }

      return (
        this.firstAttachment?.thumb_url ||
        this.firstAttachment?.thumbUrl ||
        this.firstAttachment?.data_url ||
        this.firstAttachment?.dataUrl ||
        ''
      );
    },
    attachmentPreviewLabel() {
      if (!this.showAttachmentPreview) {
        return this.$t(`${this.attachmentMessageContent}`);
      }

      return this.firstAttachment?.filename || this.$t(`${this.attachmentMessageContent}`);
    },
    shouldShowAttachmentThumbnail() {
      return (
        this.showAttachmentPreview &&
        this.lastMessageFileType === 'image' &&
        !!this.attachmentPreviewUrl
      );
    },
    shouldPrioritizeAttachmentPreview() {
      return this.showAttachmentPreview && this.hasAttachments;
    },
    containerClasses() {
      return this.showAttachmentPreview
        ? 'overflow-hidden min-w-0 flex items-center gap-2'
        : 'overflow-hidden min-w-0 text-ellipsis whitespace-nowrap';
    },
    isMessageSticker() {
      return (
        this.message &&
        (this.message.content_type === 'sticker' ||
          this.message.contentType === 'sticker')
      );
    },
  },
};
</script>

<template>
  <div :class="containerClasses">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block flex-shrink-0"
        icon="lock-closed"
      />
      <fluent-icon
        v-else-if="messageByAgent"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block flex-shrink-0"
        icon="arrow-reply"
      />
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block flex-shrink-0"
        icon="info"
      />
    </template>
    <span v-if="message.content && isMessageSticker" class="truncate">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        icon="image"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.image.CONTENT') }}
    </span>
    <span
      v-else-if="shouldPrioritizeAttachmentPreview"
      class="min-w-0 flex items-center gap-2"
    >
      <img
        v-if="shouldShowAttachmentThumbnail"
        :src="attachmentPreviewUrl"
        :alt="attachmentPreviewLabel"
        class="w-10 h-10 rounded object-cover flex-shrink-0"
      />
      <fluent-icon
        v-else-if="attachmentIcon && (showMessageType || showAttachmentPreview)"
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11 flex-shrink-0"
        :icon="attachmentIcon"
      />
      <span class="truncate">
        {{ attachmentPreviewLabel }}
      </span>
    </span>
    <span v-else-if="message.content" class="truncate">
      {{ parsedLastMessage }}
    </span>
    <span
      v-else-if="hasAttachments"
      class="min-w-0 flex items-center gap-2"
    >
      <fluent-icon
        v-if="attachmentIcon && (showMessageType || showAttachmentPreview)"
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11 flex-shrink-0"
        :icon="attachmentIcon"
      />
      <span class="truncate">
        {{ attachmentPreviewLabel }}
      </span>
    </span>
    <span v-else class="truncate">
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
